import React, { useState, useEffect } from "react";
import { api } from "../actions/api";
import { toast } from "react-toastify";
import useUserStore from "../zustand-stores/UserStore";

export default function AddMemberGroupModal({ groupId }) {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [suggestedUsers, setSuggestedUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isAddingMembers, setIsAddingMembers] = useState(false);

  const { user } = useUserStore();

  // Fetch users based on search query
  useEffect(() => {
    const searchUsers = async () => {
      if (searchQuery.trim() === "") {
        setSuggestedUsers([]);
        return;
      }

      setIsLoading(true);
      try {
        const response = await api.get("user/list", {
          params: { search: searchQuery },
        });

        // Filter out the current user if their id matches any in the results
        const filteredUsers = (response.data.data.users || []).filter(
          (suggestedUser) => suggestedUser.id !== user?.id,
        );

        setSuggestedUsers(filteredUsers);
      } catch (error) {
        console.error("Error searching users:", error);
        toast.error("Failed to search users");
      } finally {
        setIsLoading(false);
      }
    };

    const debounceTimer = setTimeout(searchUsers, 500);
    return () => clearTimeout(debounceTimer);
  }, [searchQuery]);

  const removeMember = (userId) => {
    const memberToRemove = selectedMembers.find(
      (member) => member.id === userId,
    );
    setSelectedMembers(
      selectedMembers.filter((member) => member.id !== userId),
    );

    if (memberToRemove) {
      setSuggestedUsers((prev) => [...prev, memberToRemove]);
    }
  };

  const handleAddMember = (user) => {
    if (!selectedMembers.some((member) => member.id === user.id)) {
      setSelectedMembers([...selectedMembers, user]);
      setSuggestedUsers(suggestedUsers.filter((u) => u.id !== user.id));
    }
  };

  const handleAddMembersToGroup = async () => {
    if (!groupId || selectedMembers.length === 0) return;

    setIsAddingMembers(true);
    try {
      const memberIds = selectedMembers.map((member) => member.id);
      const formData = new FormData();
      formData.append("group_id", groupId);
      memberIds.forEach((id) => formData.append("members[]", id));

      await api.post("user/add-members", formData);

      toast.success("Members added successfully!");
      // Close modal
      document
        .getElementById("addMemberGroupModal")
        ?.querySelector(".btn-close")
        ?.click();
      // Reset state
      setSelectedMembers([]);
      setSearchQuery("");
      setSuggestedUsers([]);
    } catch (error) {
      console.error("Error adding members:", error);
      toast.error(error.response?.data?.message || "Failed to add members");
    } finally {
      setIsAddingMembers(false);
    }
  };

  return (
    <div
      className="modal add-task-modal fade"
      id="addMemberGroupModal"
      tabIndex={-1}
      aria-labelledby="addNewTaskModalLabel"
      aria-hidden="true"
    >
      <div className="modal-dialog modal-dialog-centered">
        <div
          className="modal-content"
          style={{ maxWidth: "100%", width: "540px", background: "#132534" }}
        >
          <div className="modal-header">
            <h1
              className="modal-title text-heading-h3"
              style={{ color: "#fff" }}
            >
              Add Member
            </h1>
            <button
              type="button"
              className="btn-close btn-close-white position-absolute end-0 me-3"
              data-bs-dismiss="modal"
              aria-label="Close"
            ></button>
          </div>
          <hr className="modal-hr" />
          <div className="modal-body">
            <div className="row">
              {/* Search and Add Button Row */}
              <div className="col-md-12 mb-3 d-flex gap-2">
                <input
                  type="text"
                  className="form-control flex-grow-1"
                  placeholder="Search users..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  style={{
                    backgroundColor: "#1B2C3A",
                    border: "none",
                    color: "white",
                    padding: "10px 15px",
                    borderRadius: "30px",
                  }}
                />
                <button
                  className="btn btn-primary"
                  onClick={handleAddMembersToGroup}
                  disabled={selectedMembers.length === 0 || isAddingMembers}
                  style={{
                    backgroundColor: "#008CFF",
                    border: "none",
                    borderRadius: "33px",
                    padding: "0 20px",
                    minWidth: "100px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "6px",
                  }}
                >
                  {isAddingMembers ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm"
                        role="status"
                        aria-hidden="true"
                      ></span>
                      Adding
                    </>
                  ) : (
                    <>
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M12.6668 8.66732H8.66683V12.6673H7.3335V8.66732H3.3335V7.33398H7.3335V3.33398H8.66683V7.33398H12.6668V8.66732Z"
                          fill="white"
                        />
                      </svg>
                      Add
                    </>
                  )}
                </button>
              </div>

              {/* Selected Members */}
              <div className="col-md-12 mb-4">
                <div
                  className="add-member-section"
                  style={{
                    padding: "12px",
                    borderRadius: "33px",
                    backgroundColor: "#1B2C3A",
                    minHeight: "60px",
                  }}
                >
                  {selectedMembers.length > 0 ? (
                    <div className="d-flex flex-wrap gap-2">
                      {selectedMembers.map((member) => (
                        <div
                          key={member.id}
                          className="member-chip d-flex align-items-center"
                          style={{
                            backgroundColor: "#008CFF",
                            padding: "4px 8px",
                            borderRadius: "33px",
                            color: "white",
                            width: "fit-content",
                          }}
                        >
                          <img
                            src={member.image}
                            alt={member.full_name}
                            style={{
                              width: "24px",
                              height: "24px",
                              borderRadius: "50%",
                              marginRight: "6px",
                            }}
                          />
                          <span style={{ marginRight: "6px" }}>
                            {member.full_name}
                          </span>
                          <button
                            onClick={() => removeMember(member.id)}
                            style={{
                              background: "none",
                              border: "none",
                              color: "white",
                              cursor: "pointer",
                              padding: 0,
                            }}
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p style={{ color: "#7E8897" }}>No members selected</p>
                  )}
                </div>
              </div>

              {/* Suggested Members */}
              <div className="col-md-12">
                {isLoading ? (
                  <div className="text-center py-3">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                ) : suggestedUsers.length > 0 ? (
                  <div
                    style={{
                      maxHeight: "300px",
                      overflowY: "auto",
                      paddingRight: "10px",
                    }}
                  >
                    {suggestedUsers.map((user) => (
                      <div
                        key={user.id}
                        className="d-flex align-items-center justify-content-between gap-3 mb-3 p-2"
                        style={{
                          cursor: "pointer",
                          borderRadius: "12px",
                          backgroundColor: "#1B2C3A",
                          transition: "background-color 0.2s",
                        }}
                        onClick={() => handleAddMember(user)}
                        onMouseEnter={(e) =>
                          (e.currentTarget.style.backgroundColor = "#243748")
                        }
                        onMouseLeave={(e) =>
                          (e.currentTarget.style.backgroundColor = "#1B2C3A")
                        }
                      >
                        <div className="d-flex align-items-center gap-3">
                          <img
                            src={user.image}
                            alt={user.full_name}
                            style={{
                              width: "40px",
                              height: "40px",
                              borderRadius: "50%",
                              objectFit: "cover",
                            }}
                          />
                          <div>
                            <div style={{ color: "white", fontWeight: "500" }}>
                              {user.full_name}
                            </div>
                            <div style={{ color: "#7E8897", fontSize: "12px" }}>
                              @{user.slug}
                            </div>
                          </div>
                        </div>
                        <button
                          className="btn btn-sm"
                          style={{
                            backgroundColor: "#008CFF",
                            color: "white",
                            border: "none",
                            borderRadius: "33px",
                            padding: "4px 12px",
                          }}
                        >
                          Add
                        </button>
                      </div>
                    ))}
                  </div>
                ) : searchQuery ? (
                  <p style={{ color: "#7E8897" }}>No users found</p>
                ) : (
                  <p style={{ color: "#7E8897" }}>Search for users to add</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
